import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { staffingAPI } from '@/services/staffingAPI';
import { Upload, Trash2, Search, Loader2, CheckCircle2, XCircle, Users, Filter } from 'lucide-react';
import Pagination from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

const VISIBILITY_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'actively_looking', label: 'Actively Looking' },
  { value: 'exploring', label: 'Exploring' },
  { value: 'not_looking', label: 'Not Looking' },
];

export default function StaffingCandidates() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [visibility, setVisibility] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await staffingAPI.listCandidates({ page, limit: pageSize, search, visibility });
      setRows(d.candidates || []);
      setTotal(d.total || 0);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, pageSize, visibility]);

  const onBulkUpload = async (e) => {
    const fs = Array.from(e.target.files || []);
    if (!fs.length) return;
    if (fs.length > 10) { alert('Up to 10 resumes at a time'); return; }
    setUploading(true);
    setUploadResults(null);
    try {
      const r = await staffingAPI.bulkUpload(fs);
      setUploadResults(r);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const toggleVisibility = async (cand, next) => {
    try {
      await staffingAPI.updateCandidate(cand.id, { visibility_status: next });
      setRows(rs => rs.map(r => r.id === cand.id ? { ...r, visibility_status: next } : r));
    } catch (err) { alert(err?.response?.data?.detail || 'Update failed'); }
  };

  const onDelete = async (cand) => {
    if (!window.confirm(`Remove ${cand.name || cand.email}?`)) return;
    try { await staffingAPI.deleteCandidate(cand.id); await load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="staffing-candidates">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Users className="w-5 h-5" /> Candidates</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">{total} candidate{total === 1 ? '' : 's'} on your roster</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" multiple className="hidden" onChange={onBulkUpload} data-testid="bulk-upload-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-full text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            data-testid="bulk-upload-btn"
          >
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</> : <><Upload className="w-4 h-4" /> Bulk upload (max 10)</>}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (setPage(1), load())}
            placeholder="Search by name or email…"
            className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600"
            data-testid="candidate-search-input"
          />
        </div>
        <select value={visibility} onChange={e => { setPage(1); setVisibility(e.target.value); }} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="candidate-visibility-filter">
          {VISIBILITY_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
        <button onClick={() => { setPage(1); load(); }} className="px-3 py-2 bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 rounded-lg text-sm text-slate-700 dark:text-zinc-200 flex items-center gap-1.5" data-testid="apply-filters-btn"><Filter className="w-3.5 h-3.5" /> Apply</button>
      </div>

      {uploadResults && (
        <div className="bg-white dark:bg-[#0e0e10] border border-slate-200 dark:border-zinc-800 rounded-xl p-4 mb-4" data-testid="upload-results">
          <p className="text-sm text-slate-900 dark:text-white mb-2">Uploaded {uploadResults.uploaded} of {uploadResults.total}</p>
          <ul className="space-y-1.5 text-xs">
            {uploadResults.results.map((r, i) => (
              <li key={i} className={`flex items-start gap-2 ${r.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> : <XCircle className="w-3.5 h-3.5 mt-0.5" />}
                <span className="break-all"><span className="font-medium">{r.file}</span> — {r.ok ? `${r.name} (${r.email})` : r.error}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ScrollHint>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-white dark:bg-[#0e0e10] text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Skills</th>
              <th className="text-left p-3">Rate Card</th>
              <th className="text-left p-3">Visibility</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-slate-500 dark:text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500 dark:text-zinc-500">No candidates yet — upload resumes to get started.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-200 dark:border-zinc-800/50 hover:bg-slate-50 dark:hover:bg-zinc-900/30" data-testid={`candidate-row-${r.id}`}>
                <td className="p-3 text-slate-900 dark:text-white"><Link to={`/staffing/candidates/${r.id}`} className="hover:text-emerald-700 dark:hover:text-emerald-300">{r.name || '—'}</Link></td>
                <td className="p-3 text-slate-500 dark:text-zinc-400 text-xs">{r.email}</td>
                <td className="p-3 text-slate-500 dark:text-zinc-400 text-xs">{(r.skills || []).slice(0, 4).join(', ')}{r.skills?.length > 4 && ` +${r.skills.length - 4}`}</td>
                <td className="p-3 text-slate-600 dark:text-zinc-300 text-xs">
                  {r.expected_compensation_min || r.expected_compensation_max
                    ? `${r.expected_compensation_min || 0}–${r.expected_compensation_max || '∞'} ${r.expected_compensation_currency || 'USD'}`
                    : <span className="text-slate-500 dark:text-zinc-600">—</span>}
                </td>
                <td className="p-3">
                  <select
                    value={r.visibility_status}
                    onChange={e => toggleVisibility(r, e.target.value)}
                    className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-2 py-1 text-xs text-slate-600 dark:text-zinc-300"
                    data-testid={`visibility-select-${r.id}`}
                  >
                    <option value="actively_looking">Actively Looking</option>
                    <option value="exploring">Exploring</option>
                    <option value="not_looking">Not Looking</option>
                  </select>
                </td>
                <td className="p-3 text-right">
                  <Link to={`/staffing/candidates/${r.id}`} className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 mr-3">Edit</Link>
                  <button onClick={() => onDelete(r)} className="text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400" data-testid={`delete-candidate-${r.id}`}><Trash2 className="w-3.5 h-3.5 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </ScrollHint>
      {total > 20 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          testIdPrefix="staffing-cand-pg"
        />
      )}
    </div>
  );
}
